import { useEffect, useState } from 'react';
import { logger } from '../lib/logger';
declare global {
  interface Window {
    alloy: Alloy;
  }
}

export type Alloy = {
  <T = void>(event: AdobeWebSDKCommand, opt1?: unknown): Promise<T>;
};

type AdobeWebSDKCommand =
  | 'configure'
  | 'event'
  | 'setConsent'
  | 'getIdentity'
  | 'appendIdentityToUrl'
  | 'syncIdentity'
  | 'getLibraryInfo'
  | 'sendEvent'
  | 'setDebug'
  | 'getDebug'
  | 'getConfig'
  | 'getPropositions'
  | 'applyPropositions'
  | 'sendPropositionInteraction'
  | 'getDecisionScopes'
  | 'getDecisions'
  | 'applyDecisions'
  | 'sendEventWithPropositions';

export type Identity = { identity: { ECID: string }; edge: { regionId: number } };
export type IdentityResponse = { identity: Identity };
type LibraryInfo = { version: string; commands: AdobeWebSDKCommand[]; configs: Configs };
type LibraryInfoResponse = { libraryInfo: LibraryInfo };

type Configs = {
  thirdPartyCookiesEnabled: boolean;
  idMigrationEnabled: boolean;
  personalizationStorageEnabled: boolean;
  targetMigrationEnabled: boolean;
  autoCollectPropositionInteractions: {
    AJO: string;
    TGT: string;
  };
  context: string[];
  defaultConsent: string;
  clickCollectionEnabled: boolean;
  clickCollection: {
    internalLinkEnabled: boolean;
    externalLinkEnabled: boolean;
    downloadLinkEnabled: boolean;
    sessionStorageEnabled: boolean;
    eventGroupingEnabled: boolean;
  };
  onBeforeEventSend: (content: { xdm: { eventType: string } }) => void;
  downloadLinkQualifier: string;
  debugEnabled: boolean;
  datastreamId: string;
  edgeDomain: string;
  edgeBasePath: string;
  orgId: string;
};

export function useAlloy() {
  const { log } = logger({})('useAlloy');
  const [alloy, setAlloy] = useState<{ alloy?: Alloy }>({ alloy: undefined });

  useEffect(() => {
    (async () => {
      if (!window.alloy) {
        log.info('Alloy is not initialized');
        setAlloy({
          alloy: () => Promise.reject(new Error('Alloy Not loaded')),
        });
        return;
      }
      const { libraryInfo } = await window.alloy<LibraryInfoResponse>('getLibraryInfo');
      log.info('Alloy initialized', libraryInfo);
      setAlloy({ alloy: window.alloy });
    })();
  }, []);

  return alloy;
}
